import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Paper,
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from './AuthModule';
import { auth as authApi } from '../../utils/api';

const RegisterComponent = () => {
  const { register, loading, error } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    accessCode: ''
  });
  const [validationError, setValidationError] = useState('');
  const [codeVerified, setCodeVerified] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setValidationError('');
  };

  const verifyAccessCode = async () => {
    if (!formData.accessCode.trim()) {
      setValidationError('Access code is required');
      return;
    }

    try {
      setVerifyingCode(true);
      const response = await authApi.verifyAccessCode(formData.accessCode);
      
      if (response.valid) {
        setCodeVerified(true);
        setShowRegistrationForm(true);
        setValidationError('');
      } else {
        setValidationError('Invalid access code');
      }
    } catch (error) {
      setValidationError(error.response?.data?.message || 'Failed to verify access code');
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!codeVerified) {
      setValidationError('Please verify your access code first');
      return;
    }

    if (!formData.username.trim()) {
      setValidationError('Username is required');
      return;
    }

    if (formData.username.length < 3) {
      setValidationError('Username must be at least 3 characters long');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setValidationError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setValidationError('Password must be at least 8 characters long');
      return;
    }

    await register(formData.email, formData.password, formData.username, formData.accessCode);
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            Create Account
          </Typography>
          {(error || validationError) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error || validationError}
            </Alert>
          )}
          
          {!showRegistrationForm ? (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body1" gutterBottom>
                Please enter your access code to continue
              </Typography>
              <TextField
                required
                fullWidth
                id="accessCode"
                label="Access Code"
                name="accessCode"
                value={formData.accessCode}
                onChange={handleChange}
                disabled={verifyingCode}
                sx={{ mb: 2 }}
              />
              <Button
                fullWidth
                variant="contained"
                onClick={verifyAccessCode}
                disabled={verifyingCode}
                sx={{ mt: 1, mb: 2 }}
              >
                {verifyingCode ? <CircularProgress size={24} /> : 'Verify Code'}
              </Button>
              <Button
                component={RouterLink}
                to="/login"
                fullWidth
                variant="text"
                disabled={verifyingCode}
              >
                Already have an account? Sign in
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    id="username"
                    label="Username"
                    name="username"
                    autoComplete="username"
                    value={formData.username}
                    onChange={handleChange}
                    disabled={loading}
                    helperText="Username must be at least 3 characters long"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    id="email"
                    label="Email Address"
                    name="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    name="password"
                    label="Password"
                    type="password"
                    id="password"
                    autoComplete="new-password"
                    value={formData.password}
                    onChange={handleChange}
                    disabled={loading}
                    helperText="Password must be at least 8 characters long"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    name="confirmPassword"
                    label="Confirm Password"
                    type="password"
                    id="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Grid>
              </Grid>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Sign Up'}
              </Button>
              <Button
                component={RouterLink}
                to="/login"
                fullWidth
                variant="text"
                disabled={loading}
              >
                Already have an account? Sign in
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default RegisterComponent; 